import { useLocation, useHistory } from 'react-router-dom';
import * as React from 'react';
import { Box } from 'rebass';

import { ErrorReporterContext } from '@mdlinx/frontend-shared';
import { ErrorNotification, OrDivider, FormHeader, ButtonLarge } from '@mdlinx/component-library';

import { LoginWithPasswordError, useMeQuery } from 'generated/client';
import { useLoginWithPassword } from 'lib/effect-hooks';
import { messages } from 'lib/messages';

import { LoginForm, LoginFormProps } from 'app/components/login-forms';

import { trackEvent, getLocationDetails } from 'lib/analytics';
import { FormContainer } from 'app/components/layout/form-container';

const { useState, useContext } = React;

export const PasswordLoginForm: React.FC<{
  onLogin: () => void;
  onClickLoginHelp?: () => void;
  socialButtons: React.ReactNode;
  errorMessage?: string;
}> = ({ onLogin, onClickLoginHelp = () => {}, socialButtons, errorMessage }) => {
  const loginWithPassword = useLoginWithPassword();
  const errorReporter = useContext(ErrorReporterContext);
  const [error, setError] = useState<string | undefined>(errorMessage);
  const location = useLocation();
  const history = useHistory();
  const meQueryResult = useMeQuery({ fetchPolicy: 'network-only' });

  const onSubmit: LoginFormProps['onSubmit'] = (values, actions) => {
    const { email, password } = values;
    setError(undefined);

    return loginWithPassword({ email, password })
      .then(
        result => {
          if (result.success) {
            meQueryResult.refetch().catch(e => errorReporter.report(e));
            trackEvent('auth_login_succeeded', {
              action: 'onSubmit',
              ...getLocationDetails(location.pathname, location.search),
            });
            onLogin();
          } else {
            trackEvent('auth_login_failed', {
              action: 'onSubmit',
              ...getLocationDetails(location.pathname, location.search),
            });
            switch (result.error) {
              case LoginWithPasswordError.IncorrectCredentials:
                return setError(messages.error.login.incorrectCredentials);
              case LoginWithPasswordError.UserDisabled:
                return setError(messages.error.login.userDisabled);
              case LoginWithPasswordError.TooManyAttemptsTryLater:
                return setError(messages.error.login.tooManyAttemptsTryLater);
              case LoginWithPasswordError.Unknown:
                return setError(messages.error.login.unknown);
            }
          }
        },
        e => {
          errorReporter.report(e);
          setError(messages.error.login.unknown);
          trackEvent('auth_login_failed', {
            action: 'onSubmit',
            ...getLocationDetails(location.pathname, location.search),
          });
        },
      )
      .finally(() => actions.setSubmitting(false));
  };

  return (
    <FormContainer alternativeScene="sign-up">
      <FormHeader>Log in</FormHeader>
      <Box mt="12px">
        {error && (
          <Box mb="20px">
            <ErrorNotification errorMessage={error} />
          </Box>
        )}
        <LoginForm
          onSubmit={onSubmit}
          onClickLoginHelp={() => {
            trackEvent('auth_click', {
              action: 'onClickLoginHelp',
              ...getLocationDetails(location.pathname, location.search),
            });
            onClickLoginHelp();
          }}
        />
      </Box>
      <OrDivider />
      <ButtonLarge onClick={() => history.push('/login/auto-login-link')}>Send me an auto-login link</ButtonLarge>
      {socialButtons}
    </FormContainer>
  );
};
